import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["error", "loading"];
import { useSearchParams } from 'marketplace-ui-common/hooks/useSearchParams';
import { useCallback, useMemo } from 'react';
import { useFilterConfig } from '../hooks/useFilterConfig';
import { findFilter, getFilterOptions, isValidFilterValue } from './filterUtils';
const KEYS_TO_KEEP = new Set(['search', 'queryType', 'sort']);
function buildFieldPossibleValues(filters) {
  return Object.values(filters).filter(f => !!f).map(f => {
    var _f$label;
    return {
      value: f.field,
      label: (_f$label = f.label) !== null && _f$label !== void 0 ? _f$label : f.field
    };
  });
}
function buildValuePossibleValues(filters) {
  return Object.values(filters).filter(f => !!f).flatMap(f => {
    const options = getFilterOptions(f);
    if (options.length > 0) {
      return options.map(o => {
        var _o$text;
        return {
          value: o.value,
          label: `${f.field}: ${(_o$text = o.text) !== null && _o$text !== void 0 ? _o$text : o.value}`
        };
      });
    }
    if (f.dataType === 'BOOLEAN') {
      return [{
        value: 'true',
        label: `${f.field}: true`
      }, {
        value: 'false',
        label: `${f.field}: false`
      }];
    }
    return [];
  });
}
export const useFilterActionsModule = () => {
  const params = useSearchParams();
  const _useFilterConfig = useFilterConfig(),
    {
      error,
      loading
    } = _useFilterConfig,
    filterConfig = _objectWithoutPropertiesLoose(_useFilterConfig, _excluded);
  const applyFilter = useCallback(({
    field,
    value
  }) => {
    const filter = findFilter(filterConfig, field);
    if (!filter) return;
    if (!isValidFilterValue(filter, value)) return;
    if (filter.productType) {
      const currentProductType = params.get('PRODUCT_TYPE');
      if (!currentProductType) {
        params.set('PRODUCT_TYPE', filter.productType);
      }
    }
    params.set(field, value, {
      defaultValue: filter.defaultValue
    });
    params.delete('page');
  }, [params, filterConfig]);
  const removeFilter = useCallback(({
    field
  }) => {
    params.delete(field);
    params.delete('page');
  }, [params]);
  const clearAllFilters = useCallback(() => {
    params.keys().forEach(key => {
      if (!KEYS_TO_KEEP.has(key)) {
        params.delete(key);
      }
    });
  }, [params]);
  const currentFilters = useMemo(() => {
    const applied = {};
    const allParams = params.getAll();
    const filterFields = new Set(Object.values(filterConfig).filter(f => !!f).map(f => f.field));
    for (const [key, values] of Object.entries(allParams)) {
      if (filterFields.has(key) && values.length > 0) {
        applied[key] = values.join(', ');
      }
    }
    return JSON.stringify(applied);
  }, [params, filterConfig]);
  const toolParameterMeta = useMemo(() => ({
    fieldPossibleValues: buildFieldPossibleValues(filterConfig),
    valuePossibleValues: buildValuePossibleValues(filterConfig)
  }), [filterConfig]);
  return useMemo(() => {
    if (loading || error) return [];
    const {
      fieldPossibleValues,
      valuePossibleValues
    } = toolParameterMeta;
    return [{
      name: 'applyFilter',
      function: applyFilter,
      functionDescription: 'Apply a filter to the marketplace search results. You MUST only use values from the possibleValues lists. Do not invent or abbreviate values.',
      functionParameters: {
        field: {
          type: 'string',
          description: 'The filter field name.',
          possibleValues: fieldPossibleValues
        },
        value: {
          type: 'string',
          currentValue: currentFilters,
          description: 'The value to set for the given filter field.',
          possibleValues: valuePossibleValues
        }
      },
      autoApplySetting: 'always-auto-apply'
    }, {
      name: 'removeFilter',
      function: removeFilter,
      functionDescription: 'Remove a specific filter from the marketplace search results. Use this when the user wants to broaden their results by removing a previously applied filter.',
      functionParameters: {
        field: {
          type: 'string',
          description: 'The filter field name.',
          possibleValues: fieldPossibleValues
        }
      },
      autoApplySetting: 'always-auto-apply'
    }, {
      name: 'clearAllFilters',
      function: clearAllFilters,
      functionDescription: 'Clear all applied filters at once, resetting the search results to show all products. Use this when the user wants to start over or see everything.',
      functionParameters: {},
      autoApplySetting: 'always-auto-apply'
    }];
  }, [loading, error, applyFilter, removeFilter, clearAllFilters, currentFilters, toolParameterMeta]);
};